import SettingsIcon from "@mui/icons-material/Settings";
import Box from "@mui/joy/Box";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import IconButton from "@mui/joy/IconButton";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import React from "react";
import { ParticipantByState, Study } from "../../../models/study";
import { formatAndCapitalizeString } from "../../../shared/utils/text";
import CohortSmallCard from "../components/CohortSmallCard";
import StudyStateCard from "../components/StudyStateCard";

interface OverviewTabProps {
  study: Study;
  participantByState: ParticipantByState[];
  toggleCohortDrawer: () => void;
  handleUpdateStudy: (study: Study) => void;
  setEditStudy: React.Dispatch<React.SetStateAction<boolean>>;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  study,
  participantByState,
  toggleCohortDrawer,
  handleUpdateStudy,
  setEditStudy,
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1000,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(480px, 1fr))",
        gap: 2,
      }}
    >
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography level="title-md">Project details</Typography>
            <IconButton variant="plain" onClick={() => setEditStudy(true)} color="neutral">
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Typography level="body-sm">
            {study.description ? study.description : "No description provided."}
          </Typography>
          <Typography level="body-sm" color="neutral">
            Phase: {study.phase ? formatAndCapitalizeString(study.phase) : "Not specified"}
          </Typography>
          <Typography level="body-sm" color="neutral">
            Sponsor:{" "}
            {study.associated_parties.filter((ap) => ap.role === "sponsor").length > 0
              ? study.associated_parties
                  .filter((ap) => ap.role === "sponsor")
                  .map((ap, index) => (
                    <span key={`sponsor-${ap.name}-${index}`}>
                      {formatAndCapitalizeString(ap.name)}
                    </span>
                  ))
              : "Not specified"}
          </Typography>
          <Typography level="body-sm" color="neutral">
            Principal investigator:{" "}
            {study.associated_parties.filter((ap) => ap.role === "principal-investigator").length >
            0
              ? study.associated_parties
                  .filter((ap) => ap.role === "principal-investigator")
                  .map((ap, index) => (
                    <span key={`pi-${ap.name}-${index}`}>{formatAndCapitalizeString(ap.name)}</span>
                  ))
              : "Not specified"}
          </Typography>
        </CardContent>
      </Card>
      <StudyStateCard study={study} refresh={() => {}} />

      <Card variant="outlined">
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Linked cohorts
          </Typography>
          <CohortSmallCard
            study={study}
            actionButton={toggleCohortDrawer}
            openCohortDrawer={false}
            handleUpdateStudy={handleUpdateStudy}
          />
        </CardContent>
      </Card>
      {participantByState.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Participants by status
            </Typography>
            {participantByState.length > 0 && (
              <Stack direction="column" gap={1}>
                {participantByState.map((pbs, index) => (
                  <Stack
                    key={`${pbs.state}-${pbs.count}`}
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Typography level="body-md">{formatAndCapitalizeString(pbs.state)}</Typography>
                    <Typography level="body-md">{pbs.count}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default OverviewTab;
